import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useCurrentProvider } from '../hooks/useProvider';
import {
  useEndorsementStatistics,
  useReceivedEndorsements,
  useSentEndorsements,
  useProvidersToEndorse,
  usePendingEndorsements,
  useAcceptEndorsement,
  useRejectEndorsement,
} from '../hooks/useEndorsements';
import { Avatar, EndorsementCard } from '../components/EndorsementCard';
import EndorseModal from '../components/EndorseModal';
import NotAvailable from '../components/Common/NotAvailable';
import { EndorsementPublic, EndorsementWithProviders } from '../types/endorsement';
import { ProviderPublic as ProviderToEndorse } from '../types/provider';
import { getApiErrorMessage } from '../utils/errors';

// Import local images
import cloutLogo from '../assets/logos/clout-logo.png';

// Import icons
import {
  SearchIcon,
  NotificationIcon,
  UserIcon,
  StarIcon,
  BriefcaseIcon,
  LocationIcon,
  SettingsIcon,
  LogoutIcon,
  UserGroupIcon,
  SendIcon,
  ClockIcon,
  TrophyIcon,
} from '../components/icons';

type EndorsementItem = EndorsementPublic | ProviderToEndorse | EndorsementWithProviders;

const CheckMark = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path
      d="M10 3L4.5 8.5L2 6"
      stroke="white"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Endorsements: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('received');
  const [actionError, setActionError] = useState<string | null>(null);
  const [endorseTarget, setEndorseTarget] = useState<{ id: number; name: string } | null>(null);
  const endorsedSinceOpen = useRef(false);

  const { provider } = useCurrentProvider();
  const { statistics, loading: statsLoading, error: statsError } = useEndorsementStatistics();
  const { endorsements: receivedEndorsements, loading: receivedLoading } =
    useReceivedEndorsements();
  const { endorsements: sentEndorsements, loading: sentLoading } = useSentEndorsements();
  const { endorsements: pendingEndorsements, loading: pendingLoading } = usePendingEndorsements();
  const { providers: providersToEndorse, loading: providersLoading } = useProvidersToEndorse();
  const { acceptEndorsement } = useAcceptEndorsement();
  const { rejectEndorsement } = useRejectEndorsement();

  const handleLogout = () => {
    logout();
    navigate('/signin');
  };

  const handleEndorseProvider = (providerId: number) => {
    const target = providersToEndorse.find((p) => p.id === providerId);
    if (!target) return;
    endorsedSinceOpen.current = false;
    setEndorseTarget({ id: target.id, name: target.business_name });
  };

  const handleEndorseModalClose = () => {
    setEndorseTarget(null);
    if (endorsedSinceOpen.current) {
      endorsedSinceOpen.current = false;
      // Deferred so the modal's own "back to homepage" navigation lands first;
      // the reload then refreshes whichever page the user ends up on.
      setTimeout(() => window.location.reload(), 0);
    }
  };

  const handleAcceptEndorsement = async (endorsementId: number) => {
    setActionError(null);
    try {
      await acceptEndorsement(endorsementId);
      window.location.reload();
    } catch (error) {
      setActionError(getApiErrorMessage(error, 'Failed to accept endorsement'));
    }
  };

  const handleRejectEndorsement = async (endorsementId: number) => {
    setActionError(null);
    try {
      await rejectEndorsement(endorsementId);
      window.location.reload();
    } catch (error) {
      setActionError(getApiErrorMessage(error, 'Failed to reject endorsement'));
    }
  };

  const displayName = provider?.business_name || user?.username || '';
  const completion = provider?.profile_completion_percentage ?? 0;
  // Same checks as the dashboard's "Complete your Profile" checklist
  const checklist = provider
    ? [
        { label: 'Add Basic Details', done: !!provider.business_name },
        { label: 'Add Profile Photo', done: !!provider.user_profile_image_url },
        { label: 'Add Education', done: (provider.education?.length ?? 0) > 0 },
        { label: 'Add Experience', done: (provider.years_of_experience ?? 0) > 0 },
        { label: 'Endorse a Provider', done: statistics.endorsements_given > 0 },
      ]
    : [];

  const renderStat = (value: number) => {
    if (statsLoading) return '...';
    if (statsError) return <NotAvailable className="text-sm font-normal" />;
    return value;
  };

  let currentEndorsements: EndorsementItem[] = [];
  let isLoading = false;

  if (activeTab === 'received') {
    currentEndorsements = receivedEndorsements;
    isLoading = receivedLoading;
  } else if (activeTab === 'sent') {
    currentEndorsements = sentEndorsements;
    isLoading = sentLoading;
  } else if (activeTab === 'get-endorsed') {
    currentEndorsements = providersToEndorse;
    isLoading = providersLoading;
  } else if (activeTab === 'pending') {
    currentEndorsements = pendingEndorsements;
    isLoading = pendingLoading;
  }

  return (
    <div className="min-h-screen bg-[#fcfaf8]">
      <header className="bg-[#f8f6f2]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-3 sm:py-4">
            <div className="flex items-center">
              <img src={cloutLogo} alt="CLOUT Aesthetics" className="h-10 sm:h-14" />
            </div>

            <div className="hidden md:flex flex-1 max-w-lg mx-8">
              <div className="relative w-full">
                <input
                  type="text"
                  placeholder="Search for providers"
                  className="w-full px-4 py-2 pr-10 text-sm font-['Open_Sans'] text-[#1f1f1f] placeholder-[#767472] bg-white border border-[#c6a664] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#c6a664] focus:border-transparent"
                />
                <button className="absolute right-2 top-1/2 transform -translate-y-1/2">
                  <SearchIcon />
                </button>
              </div>
            </div>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <button className="p-2 hover:bg-white/50 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center">
                <NotificationIcon />
              </button>
              <button
                onClick={() => provider && navigate(`/provider/${provider.id}`)}
                className="p-2 hover:bg-white/50 rounded-full transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
              >
                <UserIcon />
              </button>
            </div>
          </div>
        </div>
      </header>

      <nav className="bg-white border-t border-b border-[#eaeaea]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center overflow-x-auto no-scrollbar">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-[#1f1f1f] uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors whitespace-nowrap min-h-[44px]"
            >
              HOME
            </button>
            <button className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-white uppercase bg-[#1f1f1f] whitespace-nowrap min-h-[44px]">
              ENDORSEMENTS
            </button>
            <button
              onClick={() => navigate('/whats-hot')}
              className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-[#1f1f1f] uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors whitespace-nowrap min-h-[44px]"
            >
              WHAT'S HOT!
            </button>
            <button
              onClick={() => navigate('/dashboard/analytics')}
              className="px-4 sm:px-8 py-3 text-xs sm:text-sm font-['Ovo'] text-[#1f1f1f] uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors whitespace-nowrap min-h-[44px]"
            >
              ANALYTICS
            </button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-6 lg:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <div className="w-full lg:w-[280px] lg:flex-shrink-0 order-2 lg:order-1">
            <div className="bg-white rounded-lg p-4 sm:p-6 text-center shadow-sm">
              <div className="relative mb-4">
                <div className="w-20 sm:w-24 h-20 sm:h-24 rounded-full overflow-hidden border-4 border-white shadow-lg mx-auto">
                  <Avatar imageUrl={provider?.user_profile_image_url} name={displayName} />
                </div>
                {provider && provider.is_certified > 0 && (
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                    <div className="bg-[#c6a664] text-white px-2 sm:px-3 py-1 rounded-full text-xs font-['Open_Sans'] font-semibold inline-flex items-center gap-1">
                      <CheckMark />
                      Certified
                    </div>
                  </div>
                )}
              </div>

              <h2 className="font-['Ovo'] text-lg sm:text-xl text-[#1f1f1f] uppercase mb-2 sm:mb-3 mt-4 sm:mt-6">
                {displayName || <NotAvailable />}
              </h2>

              {provider && (
                <div className="space-y-1.5 sm:space-y-2 mb-4 sm:mb-6 text-[#1f1f1f]">
                  <div className="flex items-center justify-center gap-2">
                    <StarIcon />
                    <span className="font-['Open_Sans'] text-xs sm:text-sm">
                      {provider.clout_score != null ? (
                        `${provider.clout_score.toFixed(1)} Clout Score`
                      ) : (
                        <NotAvailable />
                      )}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <BriefcaseIcon />
                    <span className="font-['Open_Sans'] text-xs sm:text-sm">
                      {provider.specialties?.[0] || <NotAvailable />}
                    </span>
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <LocationIcon />
                    <span className="font-['Open_Sans'] text-xs sm:text-sm">
                      {[provider.city, provider.state].filter(Boolean).join(', ') || (
                        <NotAvailable />
                      )}
                    </span>
                  </div>
                </div>
              )}

              <div className="pt-4 sm:pt-6 border-t border-[#eaeaea]">
                <h3 className="font-['Open_Sans'] font-semibold text-sm sm:text-base text-[#1f1f1f] mb-2">
                  Complete your Profile
                </h3>
                {provider && (
                  <>
                    <p className="font-['Open_Sans'] text-xs sm:text-sm text-[#767472] mb-3 sm:mb-4">
                      {completion}% to CLOUT Certification
                    </p>

                    <div className="w-full bg-gray-200 rounded-full h-2 mb-3 sm:mb-4">
                      <div
                        className="bg-[#c6a664] h-2 rounded-full transition-all duration-300"
                        style={{ width: `${completion}%` }}
                      />
                    </div>
                    <p className="font-['Open_Sans'] text-xs text-[#767472] mb-4 sm:mb-6">
                      {completion}% complete
                    </p>

                    <ul
                      aria-label="Profile checklist"
                      className="hidden lg:block space-y-3 mb-6 text-left"
                    >
                      {checklist.map(({ label, done }) => (
                        <li key={label} className="flex items-center gap-3">
                          {done ? (
                            <div
                              role="img"
                              aria-label="Done"
                              className="w-5 h-5 rounded-full border-2 border-[#c6a664] bg-[#c6a664] flex items-center justify-center"
                            >
                              <CheckMark />
                            </div>
                          ) : (
                            <div
                              role="img"
                              aria-label="Not done"
                              className="w-5 h-5 rounded-full border-2 border-gray-300"
                            ></div>
                          )}
                          <span
                            className={`font-['Open_Sans'] text-sm ${
                              done ? 'text-[#1f1f1f]' : 'text-[#767472]'
                            }`}
                          >
                            {label}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <button
                  onClick={() =>
                    navigate(provider ? `/provider/${provider.id}` : '/profile/complete')
                  }
                  className="w-full px-4 py-2 bg-white text-[#1f1f1f] border border-[#1f1f1f] font-['Ovo'] text-sm uppercase hover:bg-[#1f1f1f] hover:text-white transition-colors mb-4 sm:mb-6 min-h-[44px]"
                >
                  COMPLETE MY PROFILE
                </button>

                <div className="hidden lg:block space-y-3 pt-4 sm:pt-6 border-t border-[#eaeaea]">
                  <button
                    onClick={() => provider && navigate(`/provider/${provider.id}`)}
                    className="flex items-center gap-3 w-full text-left hover:text-[#c6a664] transition-colors min-h-[44px]"
                  >
                    <SettingsIcon />
                    <span className="font-['Open_Sans'] text-sm text-[#1f1f1f]">SETTINGS</span>
                  </button>
                  <button
                    onClick={handleLogout}
                    className="flex items-center gap-3 w-full text-left hover:text-[#c6a664] transition-colors min-h-[44px]"
                  >
                    <LogoutIcon />
                    <span className="font-['Open_Sans'] text-sm text-[#1f1f1f]">LOG OUT</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 order-1 lg:order-2">
            <h1 className="font-['Ovo'] text-xl sm:text-2xl text-[#1f1f1f] uppercase mb-4 sm:mb-6">
              YOUR ENDORSEMENTS
            </h1>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="bg-white rounded-lg p-4 sm:p-6 text-center shadow-sm">
                <UserGroupIcon />
                <h3 className="font-['Open_Sans'] text-2xl sm:text-3xl font-semibold text-[#1f1f1f] mt-2">
                  {renderStat(statistics.endorsements_received)}
                </h3>
                <p className="font-['Open_Sans'] text-xs sm:text-sm text-[#767472]">Endorsements received</p>
              </div>
              <div className="bg-white rounded-lg p-4 sm:p-6 text-center shadow-sm">
                <SendIcon />
                <h3 className="font-['Open_Sans'] text-2xl sm:text-3xl font-semibold text-[#1f1f1f] mt-2">
                  {renderStat(statistics.endorsements_given)}
                </h3>
                <p className="font-['Open_Sans'] text-xs sm:text-sm text-[#767472]">Endorsements given</p>
              </div>
              <div className="bg-white rounded-lg p-4 sm:p-6 text-center shadow-sm">
                <ClockIcon />
                <h3 className="font-['Open_Sans'] text-2xl sm:text-3xl font-semibold text-[#1f1f1f] mt-2">
                  {renderStat(statistics.endorsements_pending)}
                </h3>
                <p className="font-['Open_Sans'] text-xs sm:text-sm text-[#767472]">Endorsements pending</p>
              </div>
              <div className="bg-white rounded-lg p-4 sm:p-6 text-center shadow-sm">
                <TrophyIcon />
                <h3 className="font-['Open_Sans'] text-2xl sm:text-3xl font-semibold text-[#1f1f1f] mt-2">
                  {renderStat(statistics.endorsed_by_top_scorers)}
                </h3>
                <p className="font-['Open_Sans'] text-xs sm:text-sm text-[#767472]">Endorsed by top scorers</p>
              </div>
            </div>

            <div className="flex gap-0 mb-4 sm:mb-6 overflow-x-auto no-scrollbar">
              <button
                onClick={() => setActiveTab('received')}
                className={`flex-1 min-w-[100px] py-3 font-['Open_Sans'] text-xs sm:text-sm uppercase transition-colors min-h-[44px] ${
                  activeTab === 'received'
                    ? 'bg-[#1f1f1f] text-white'
                    : 'bg-white text-[#1f1f1f] border border-[#eaeaea]'
                }`}
              >
                Received
              </button>
              <button
                onClick={() => setActiveTab('pending')}
                className={`flex-1 min-w-[100px] py-3 font-['Open_Sans'] text-xs sm:text-sm uppercase transition-colors min-h-[44px] ${
                  activeTab === 'pending'
                    ? 'bg-[#1f1f1f] text-white'
                    : 'bg-white text-[#1f1f1f] border border-[#eaeaea]'
                }`}
              >
                Pending
              </button>
              <button
                onClick={() => setActiveTab('sent')}
                className={`flex-1 min-w-[100px] py-3 font-['Open_Sans'] text-xs sm:text-sm uppercase transition-colors min-h-[44px] ${
                  activeTab === 'sent'
                    ? 'bg-[#1f1f1f] text-white'
                    : 'bg-white text-[#1f1f1f] border border-[#eaeaea]'
                }`}
              >
                Sent
              </button>
              <button
                onClick={() => setActiveTab('get-endorsed')}
                className={`flex-1 min-w-[100px] py-3 font-['Open_Sans'] text-xs sm:text-sm uppercase transition-colors whitespace-nowrap min-h-[44px] ${
                  activeTab === 'get-endorsed'
                    ? 'bg-[#1f1f1f] text-white'
                    : 'bg-white text-[#1f1f1f] border border-[#eaeaea]'
                }`}
              >
                Get Endorsed
              </button>
            </div>

            {actionError && (
              <div
                className="mb-4 p-3 bg-red-50 border border-red-200 rounded font-['Open_Sans'] text-sm text-red-700"
                role="alert"
              >
                {actionError}
              </div>
            )}

            {isLoading ? (
              <div className="flex justify-center items-center py-20">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c6a664] mx-auto mb-4"></div>
                  <p className="font-['Open_Sans'] text-sm text-[#767472]">Loading...</p>
                </div>
              </div>
            ) : currentEndorsements.length > 0 ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {currentEndorsements.map((item) => (
                  <EndorsementCard
                    key={item.id}
                    item={item}
                    tab={activeTab as any}
                    onAccept={handleAcceptEndorsement}
                    onReject={handleRejectEndorsement}
                    onEndorse={handleEndorseProvider}
                  />
                ))}
              </div>
            ) : (
              <>
                {activeTab === 'received' && (
                  <div className="flex flex-col items-center justify-center py-12 sm:py-20 px-4">
                    <h2 className="font-['Ovo'] text-xl sm:text-2xl text-[#1f1f1f] uppercase mb-3 sm:mb-4 text-center">
                      NO ENDORSEMENTS YET
                    </h2>
                    <p className="font-['Open_Sans'] text-sm sm:text-base text-[#767472] text-center mb-8 sm:mb-12 max-w-md">
                      Start building your reputation by connecting with other providers and
                      requesting endorsements.
                    </p>

                    <div className="relative mb-6 sm:mb-8">
                      <svg
                        className="w-[150px] h-[112px] sm:w-[200px] sm:h-[150px]"
                        viewBox="0 0 200 150"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <rect
                          x="60"
                          y="20"
                          width="80"
                          height="110"
                          rx="4"
                          fill="#f8f6f2"
                          stroke="#c6a664"
                          strokeWidth="2"
                        />
                        <rect x="70" y="10" width="60" height="20" rx="3" fill="#c6a664" />
                        <circle cx="100" cy="20" r="5" fill="#f8f6f2" />

                        <line x1="75" y1="50" x2="125" y2="50" stroke="#e0e0e0" strokeWidth="2" />
                        <line x1="75" y1="65" x2="125" y2="65" stroke="#e0e0e0" strokeWidth="2" />
                        <line x1="75" y1="80" x2="125" y2="80" stroke="#e0e0e0" strokeWidth="2" />
                        <line x1="75" y1="95" x2="125" y2="95" stroke="#e0e0e0" strokeWidth="2" />
                      </svg>
                    </div>

                    <button
                      onClick={() => navigate('/profile/complete')}
                      className="px-6 py-3 bg-[#1f1f1f] text-white font-['Ovo'] text-sm uppercase hover:bg-[#333] transition-colors min-h-[44px]"
                    >
                      COMPLETE MY PROFILE
                    </button>
                  </div>
                )}

                {activeTab === 'pending' && (
                  <div className="flex flex-col items-center justify-center py-12 sm:py-20 px-4">
                    <h2 className="font-['Ovo'] text-xl sm:text-2xl text-[#1f1f1f] uppercase mb-3 sm:mb-4 text-center">
                      NO PENDING ENDORSEMENTS
                    </h2>
                    <p className="font-['Open_Sans'] text-sm sm:text-base text-[#767472] text-center max-w-md">
                      Endorsements waiting for your approval will appear here.
                    </p>
                  </div>
                )}

                {activeTab === 'sent' && (
                  <div className="flex flex-col items-center justify-center py-12 sm:py-20 px-4">
                    <h2 className="font-['Ovo'] text-xl sm:text-2xl text-[#1f1f1f] uppercase mb-3 sm:mb-4 text-center">
                      YOU HAVEN'T ENDORSED ANYONE YET
                    </h2>
                    <p className="font-['Open_Sans'] text-sm sm:text-base text-[#767472] text-center mb-8 sm:mb-12 max-w-md">
                      Show your support to providers you trust.
                      <br />
                      Endorsements help everyone grow stronger together.
                    </p>

                    <div className="relative mb-6 sm:mb-8 flex flex-wrap items-center justify-center gap-3 sm:gap-4">
                      <div className="relative hidden sm:block">
                        <div className="w-24 sm:w-32 h-32 sm:h-40 bg-white rounded-lg shadow-sm border border-[#e0e0e0] p-3 sm:p-4 flex flex-col items-center">
                          <div className="w-10 sm:w-12 h-10 sm:h-12 bg-[#e0e0e0] rounded-full mb-2"></div>
                          <div className="w-full h-2 bg-[#e0e0e0] rounded mb-2"></div>
                          <div className="w-3/4 h-2 bg-[#e0e0e0] rounded"></div>
                        </div>
                      </div>

                      <div className="relative z-10">
                        <div className="w-32 sm:w-40 h-40 sm:h-48 bg-white rounded-lg shadow-lg border border-[#c6a664] p-3 sm:p-4 flex flex-col items-center">
                          <div className="w-12 sm:w-16 h-12 sm:h-16 bg-[#c6a664] rounded-full mb-2 sm:mb-3 flex items-center justify-center">
                            <svg
                              className="w-6 h-6 sm:w-8 sm:h-8"
                              viewBox="0 0 32 32"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M16 20C20.4183 20 24 16.4183 24 12C24 7.58172 20.4183 4 16 4C11.5817 4 8 7.58172 8 12C8 16.4183 11.5817 20 16 20Z"
                                fill="#f8f6f2"
                              />
                              <path
                                d="M16 20C12.8174 20 9.76516 21.2643 7.51472 23.5147C5.26428 25.7652 4 28.8174 4 32H28C28 28.8174 26.7357 25.7652 24.4853 23.5147C22.2348 21.2643 19.1826 20 16 20Z"
                                fill="#f8f6f2"
                              />
                            </svg>
                          </div>
                          <div className="w-full h-2 sm:h-3 bg-[#c6a664] rounded mb-2"></div>
                          <div className="w-3/4 h-1.5 sm:h-2 bg-[#e0e0e0] rounded mb-2 sm:mb-3"></div>
                          <div className="w-2/3 h-1.5 sm:h-2 bg-[#e0e0e0] rounded"></div>
                        </div>
                        <div className="absolute -top-2 -right-2 w-6 sm:w-8 h-6 sm:h-8 bg-[#c6a664] rounded-full flex items-center justify-center">
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 16 16"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M13.5 4.5L6 12L2.5 8.5"
                              stroke="white"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>

                      <div className="hidden sm:block space-y-2">
                        <div className="w-28 sm:w-32 h-20 sm:h-24 bg-white rounded-lg shadow-sm border border-[#c6a664] p-2 sm:p-3 flex items-center gap-2">
                          <div className="w-6 sm:w-8 h-6 sm:h-8 bg-[#c6a664] rounded-full flex-shrink-0"></div>
                          <div className="flex-1">
                            <div className="w-full h-1.5 sm:h-2 bg-[#c6a664] rounded mb-1"></div>
                            <div className="w-3/4 h-1 sm:h-1.5 bg-[#e0e0e0] rounded"></div>
                          </div>
                        </div>
                        <div className="w-28 sm:w-32 h-20 sm:h-24 bg-white rounded-lg shadow-sm border border-[#c6a664] p-2 sm:p-3 flex items-center gap-2">
                          <div className="w-6 sm:w-8 h-6 sm:h-8 bg-[#c6a664] rounded-full flex-shrink-0"></div>
                          <div className="flex-1">
                            <div className="w-full h-1.5 sm:h-2 bg-[#c6a664] rounded mb-1"></div>
                            <div className="w-3/4 h-1 sm:h-1.5 bg-[#e0e0e0] rounded"></div>
                          </div>
                        </div>
                        <div className="absolute -top-2 -right-2 w-5 sm:w-6 h-5 sm:h-6 bg-[#c6a664] rounded-full flex items-center justify-center">
                          <svg
                            width="12"
                            height="12"
                            viewBox="0 0 12 12"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M10 3L4.5 8.5L2 6"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => {}}
                      className="px-6 py-3 bg-[#1f1f1f] text-white font-['Ovo'] text-sm uppercase hover:bg-[#333] transition-colors min-h-[44px]"
                    >
                      SEARCH FOR PROVIDERS
                    </button>
                  </div>
                )}

                {activeTab === 'get-endorsed' && (
                  <div className="flex flex-col items-center justify-center py-12 sm:py-20 px-4">
                    <h2 className="font-['Ovo'] text-xl sm:text-2xl text-[#1f1f1f] uppercase mb-3 sm:mb-4 text-center">
                      COMPLETE YOUR PROFILE TO ENDORSE PROVIDERS
                    </h2>
                    <p className="font-['Open_Sans'] text-sm sm:text-base text-[#767472] text-center mb-8 sm:mb-12 max-w-md">
                      Build trust by completing your profile. Unlock endorsements once
                      <br />
                      your profile is ready!
                    </p>

                    <div className="relative mb-6 sm:mb-8">
                      <div className="w-32 sm:w-40 h-40 sm:h-48 bg-white rounded-lg shadow-lg border-2 border-[#1f1f1f] overflow-hidden">
                        <div className="h-12 sm:h-16 bg-[#c6a664] relative">
                          <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 translate-y-1/2 w-10 sm:w-12 h-10 sm:h-12 bg-white rounded-full flex items-center justify-center border-2 border-[#1f1f1f]">
                            <svg
                              width="24"
                              height="24"
                              viewBox="0 0 24 24"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
                                stroke="#1f1f1f"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                              <path
                                d="M12 11C14.2091 11 16 9.20914 16 7C16 4.79086 14.2091 3 12 3C9.79086 3 8 4.79086 8 7C8 9.20914 9.79086 11 12 11Z"
                                stroke="#1f1f1f"
                                strokeWidth="2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </div>
                        </div>

                        <div className="pt-6 sm:pt-8 px-3 sm:px-4 pb-3 sm:pb-4">
                          <div className="space-y-1.5 sm:space-y-2">
                            <div className="h-1.5 sm:h-2 bg-[#e0e0e0] rounded w-full"></div>
                            <div className="h-1.5 sm:h-2 bg-[#e0e0e0] rounded w-3/4"></div>
                            <div className="h-1.5 sm:h-2 bg-[#e0e0e0] rounded w-1/2"></div>
                          </div>

                          <div className="mt-3 sm:mt-4 h-1.5 sm:h-2 bg-[#c6a664] rounded w-full"></div>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => navigate('/profile')}
                      className="px-6 py-3 bg-[#1f1f1f] text-white font-['Ovo'] text-sm uppercase hover:bg-[#333] transition-colors min-h-[44px]"
                    >
                      COMPLETE MY PROFILE
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      <EndorseModal
        isOpen={endorseTarget !== null}
        onClose={handleEndorseModalClose}
        onEndorsed={() => {
          endorsedSinceOpen.current = true;
        }}
        providerName={endorseTarget?.name ?? ''}
        providerId={endorseTarget?.id ?? null}
      />
    </div>
  );
};

export default Endorsements;
